import { useState } from "react";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import api, { apiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const MANAGERS = ["store_manager", "accountant", "owner"];

function actionsFor(entity, data, role) {
  const list = [];
  if (entity === "purchases" && data.duplicate_status === "pending_review" && role !== "accountant") {
    list.push({ key: "dup-approve", label: "Approve duplicate exception", url: "duplicate-decision", body: { decision: "approve" }, needsReason: true });
    list.push({ key: "dup-decline", label: "Decline duplicate exception", url: "duplicate-decision", body: { decision: "decline" }, needsReason: true, danger: true });
  }
  if (entity === "expenses" && data.exception_status === "pending") {
    list.push({ key: "exc-approve", label: "Approve no-receipt exception", url: "exception-decision", body: { decision: "approve" }, needsReason: true });
    list.push({ key: "exc-decline", label: "Decline no-receipt exception", url: "exception-decision", body: { decision: "decline" }, needsReason: true, danger: true });
  }
  if (data.status === "submitted") {
    const why = entity === "cash-closings" && data.material;
    list.push({ key: "approve", label: why ? "Approve cash difference" : "Approve", url: "approve", body: {}, needsReason: why });
    list.push({ key: "return", label: "Return for correction", url: "reject", body: {}, needsReason: true, danger: true });
  }
  if (["submitted", "approved", "rejected"].includes(data.status)) {
    list.push({ key: "void", label: "Void record", url: "void", body: {}, needsReason: true, danger: true });
  }
  return list;
}

export default function ReviewPanel({ entity, data, onDone }) {
  const { user } = useAuth();
  const [active, setActive] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  if (!user || !MANAGERS.includes(user.role)) return null;
  const own = data.submitted_by === user.id && user.role !== "owner";
  const actions = actionsFor(entity, data, user.role).filter((a) => a.key === "void" || !own);
  if (!actions.length) return null;

  const run = async (a) => {
    setBusy(true);
    try {
      await api.patch(`/${entity}/${data.id}/${a.url}`, { ...a.body, reason: reason.trim() || null });
      toast.success("Done — recorded in history");
      setActive(null);
      setReason("");
      onDone();
    } catch (err) {
      toast.error(apiError(err, "Action failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="border-gold/30 shadow-none" data-testid="review-panel">
      <CardContent className="p-4 space-y-3">
        <h3 className="font-semibold text-navy text-sm flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-gold" /> Manager review</h3>
        {own && <p className="text-xs text-navy/50">You submitted this record, so another manager or the owner must review it.</p>}
        <div className="grid gap-2">
          {actions.map((a) => (
            <Button key={a.key} variant={a.danger ? "outline" : "default"} disabled={busy}
              className={`min-h-[48px] ${a.danger ? "text-red-600 border-red-200" : "bg-gold text-navy hover:bg-gold-light"} ${active?.key === a.key ? "ring-2 ring-gold" : ""}`}
              onClick={() => (a.needsReason ? setActive(a) : run(a))} data-testid={`review-${a.key}-btn`}>
              {a.label}
            </Button>
          ))}
        </div>
        {active && (
          <div className="space-y-2" data-testid="review-reason-form">
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} className="gm-input"
              placeholder="Written reason (required, at least 10 characters)" data-testid="review-reason-input" />
            <Button className="w-full min-h-[48px] bg-navy text-cream" disabled={busy || reason.trim().length < 10}
              onClick={() => run(active)} data-testid="review-confirm-btn">
              Confirm: {active.label}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
